import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Collapse, Spin } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  StyledCodeButton,
  StyledActionBtns,
  StyledDivider,
  StyledEncircledNumber,
  StyledSnippets,
  StyledCodeBlock,
  StyledIntegrationSteps,
  StyledIntegrationInstruction,
  StyledCollapsiblePanel,
  StyledCollapsiblePanelHeader,
  StyledPanelSubheader,
} from './Mobile.styles';
import Input from 'components/Input';
import Button from 'components/Button';
import Modal from 'components/Modals/GenericModal';
import Select from 'components/Select';
import useMobile from './hooks';
import BotCustomizer from 'components/BotCustomizer';
import { OPEN_EDIT_PANEL_INTEGRATION } from 'store/action';
import { INTEGRATION_PLATFORMS } from 'constants/integrationPlatforms';

const { Paragraph } = Typography;

const LOCKED_PANEL = 2;

const Mobile = ({ appType, isPageReady }) => {
  const {
    activePanel,
    confirmEditModal,
    currentIntegrationID,
    dispatch,
    integrationData,
    currentIntegrationSettings,
    loading,
    handleChangeFields,
    handlePanelChange,
    handleSaveChannelInfo,
    onSave,
    onUpdate,
    setBotComponent,
    setConfirmEditModal,
    setEnableEdit,
    handleBlurFormField,
  } = useMobile();
  const integrationMethods = [
    {
      label: 'Sandbox',
      value: 'sandbox',
    },
  ];

  const ConfirmEditModal = (
    <Modal
      title="Edit Channel Information"
      visible={confirmEditModal.visible}
      closable={false}
      onOk={() => {
        setEnableEdit(true);
        setConfirmEditModal({ ...confirmEditModal, visible: false });
        dispatch({
          type: OPEN_EDIT_PANEL_INTEGRATION,
          payload: { panel: LOCKED_PANEL },
        });
      }}
      okText={'Yes, Edit channel information'}
      onCancel={() => setConfirmEditModal(false)}
    >
      <p>
        Making a change in the channel information can result into a change in
        the code. Are you sure you want to edit the channel information?
      </p>
    </Modal>
  );

  const getButtonValue = useMemo(() => {
    if (!!currentIntegrationID) {
      return 'Update';
    } else if (loading) {
      return 'Processing';
    }
    return 'Save';
  }, [currentIntegrationID, loading]);

  return (
    <Spin spinning={!isPageReady || loading} tip="Just a moment...">
      <div>
        <Collapse
          activeKey={`mobile-integration-panel-${activePanel}`}
          accordion
          ghost
          onChange={e => handlePanelChange(e)}
        >
          <StyledCollapsiblePanel
            showArrow={false}
            key={'mobile-integration-panel-1'}
            header={
              <StyledCollapsiblePanelHeader>
                <StyledEncircledNumber>1</StyledEncircledNumber>
                <span>Channel information</span>
              </StyledCollapsiblePanelHeader>
            }
          >
            <StyledIntegrationSteps>
              <form onSubmit={handleSaveChannelInfo}>
                <Input
                  disabled={!!currentIntegrationID}
                  value={integrationData.identifier}
                  label="Channel Name"
                  placeholder="My Mobile integration name"
                  onChange={e =>
                    handleChangeFields('identifier', e.target.value)
                  }
                  onBlur={handleBlurFormField}
                  required
                />
                <Select
                  defaultValue={integrationData.path}
                  onChange={e => handleChangeFields('path', e)}
                  label="App Type"
                  options={appType}
                />
                <Select
                  disabled
                  defaultValue={integrationData.type}
                  onChange={e => handleChangeFields('type', e)}
                  label="Integration Platform"
                  value={INTEGRATION_PLATFORMS['mobile']}
                />
                <StyledActionBtns>
                  <StyledCodeButton value={getButtonValue} type="submit" />
                  <Button
                    variant="secondary"
                    value="Reset"
                    type="button"
                    onClick={() => handleChangeFields('clear')}
                  />
                </StyledActionBtns>
              </form>
            </StyledIntegrationSteps>
          </StyledCollapsiblePanel>
          <StyledDivider />

          <StyledCollapsiblePanel
            showArrow={false}
            key={'mobile-integration-panel-2'}
            header={
              <StyledCollapsiblePanelHeader>
                <StyledEncircledNumber>2</StyledEncircledNumber>
                <span>Personalise your bot</span>
              </StyledCollapsiblePanelHeader>
            }
          >
            {!!currentIntegrationSettings &&
            !!currentIntegrationSettings.settings ? (
              <BotCustomizer
                setBotComponent={setBotComponent}
                onSave={onSave}
                onUpdate={onUpdate}
              />
            ) : null}
          </StyledCollapsiblePanel>
          <StyledDivider />

          <StyledCollapsiblePanel
            showArrow={false}
            key={'mobile-integration-panel-3'}
            header={
              <StyledCollapsiblePanelHeader>
                <StyledEncircledNumber>3</StyledEncircledNumber>
                <span>Save and Integrate</span>
              </StyledCollapsiblePanelHeader>
            }
          >
            <StyledIntegrationSteps>
              <StyledPanelSubheader>
                You are done. Copy the code into your flutter application.
                Analytics and reports for this integration can be viewed in
                integrated channels.
              </StyledPanelSubheader>
              <Select
                defaultValue={integrationMethods[0].label}
                placeholder="Select a method"
                label="Select a integration method"
                options={integrationMethods}
                hidden
              />
              {!!currentIntegrationSettings?.settings && (
                <>
                  <StyledSnippets>
                    <StyledIntegrationInstruction>
                      Copy and paste it in page where you want to include the
                      button that will redirect on webview.
                      <CopyToClipboard text={currentIntegrationSettings?.code2}>
                        <StyledCodeButton
                          className="secondary"
                          value="Copy code"
                        />
                      </CopyToClipboard>
                    </StyledIntegrationInstruction>
                    <StyledCodeBlock>
                      <Paragraph>
                        <pre>
                          <code>{currentIntegrationSettings?.code2}</code>
                        </pre>
                      </Paragraph>
                    </StyledCodeBlock>
                    <StyledIntegrationInstruction>
                      {`Copy and paste this webview code in your dat file. This
                      also automatically open the widget.`}
                      <CopyToClipboard text={currentIntegrationSettings?.code1}>
                        <StyledCodeButton
                          className="secondary"
                          value="Copy code"
                        />
                      </CopyToClipboard>
                    </StyledIntegrationInstruction>
                    <StyledCodeBlock>
                      <Paragraph>
                        <pre>
                          <code>{currentIntegrationSettings?.code1}</code>
                        </pre>
                      </Paragraph>
                    </StyledCodeBlock>
                  </StyledSnippets>
                </>
              )}
            </StyledIntegrationSteps>
            {ConfirmEditModal}
          </StyledCollapsiblePanel>
        </Collapse>
      </div>
    </Spin>
  );
};

Mobile.propTypes = {
  appType: PropTypes.array,
  isPageReady: PropTypes.bool,
};

export default Mobile;
